import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    RadialLinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import {
    Bar,
    Bubble,
    Doughnut,
    Line,
    Pie,
    PolarArea,
    Radar,
    Scatter,
} from 'vue-chartjs';

const doughnutSegmentLabelPlugin = {
    id: 'doughnutSegmentLabel',
    afterDatasetsDraw(chart, _args, pluginOptions) {
        if (chart.config.type !== 'doughnut' || !pluginOptions?.display) {
            return;
        }

        const dataset = chart.data.datasets?.[0];
        const meta = chart.getDatasetMeta(0);

        if (!dataset || !meta?.data?.length) {
            return;
        }

        const formatter = pluginOptions.formatter ?? ((value) => `${value}%`);
        const color = pluginOptions.color ?? '#ffffff';
        const fontFamily = pluginOptions.font?.family ?? "'Inter', sans-serif";
        const fontWeight = pluginOptions.font?.weight ?? '700';
        const minCircumference = pluginOptions.minCircumference ?? 0.75;

        meta.data.forEach((arc, index) => {
            const value = Number(dataset.data?.[index] ?? 0);

            if (!value || arc.circumference < minCircumference) {
                return;
            }

            const label = formatter(value, index, chart.data.labels?.[index], dataset);

            if (!label) {
                return;
            }

            const { ctx } = chart;
            const midAngle = arc.startAngle + (arc.endAngle - arc.startAngle) / 2;
            const radius = arc.innerRadius + (arc.outerRadius - arc.innerRadius) / 2;
            const x = arc.x + Math.cos(midAngle) * radius;
            const y = arc.y + Math.sin(midAngle) * radius;
            const ringThickness = arc.outerRadius - arc.innerRadius;
            const fontSize = Math.max(10, Math.min(14, ringThickness * 0.32));

            ctx.save();
            ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;

            if (ctx.measureText(label).width > arc.circumference * radius * 0.82) {
                ctx.restore();
                return;
            }

            ctx.fillStyle = color;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, x, y);
            ctx.restore();
        });
    },
};

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    RadialLinearScale,
    doughnutSegmentLabelPlugin,
    Title,
    Tooltip
);

const components = {
    BarChart: Bar,
    BubbleChart: Bubble,
    DoughnutChart: Doughnut,
    LineChart: Line,
    PieChart: Pie,
    PolarAreaChart: PolarArea,
    RadarChart: Radar,
    ScatterChart: Scatter,
};

export default {
    install(app) {
        Object.entries(components).forEach(([name, component]) => {
            app.component(name, component);
        });
    },
};
